import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { CheckCircle, XCircle, Clock, Eye, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ApprovalQueuePage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchQueue();
    fetchCurrentUser();
  }, [page]);

  const fetchCurrentUser = async () => {
    try {
      const res = await api.getCurrentUser();
      setCurrentUser(res.data);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await api.getApprovalQueue({ page, limit: 20 });
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch approval queue:', err);
      toast.error('Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (ticket) => {
    setProcessing(true);
    try {
      const reviewer = currentUser?.name || currentUser?.email || 'Admin';
      await api.approveTicket(ticket.ticket_id, 'approve', null, reviewer);
      toast.success(`Ticket #${ticket.ticket_number} approved and closed`);
      fetchQueue();
    } catch (err) {
      console.error('Failed to approve ticket:', err);
      toast.error('Failed to approve ticket');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTicket) return;
    
    setProcessing(true);
    try {
      const reviewer = currentUser?.name || currentUser?.email || 'Admin';
      await api.approveTicket(selectedTicket.ticket_id, 'reject', rejectReason, reviewer);
      toast.success(`Ticket #${selectedTicket.ticket_number} reopened`);
      setShowRejectDialog(false);
      setSelectedTicket(null);
      setRejectReason('');
      fetchQueue();
    } catch (err) {
      console.error('Failed to reject ticket:', err);
      toast.error('Failed to reject ticket');
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (ticket) => {
    setSelectedTicket(ticket);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="approval-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Approval Queue</h1>
          <p className="text-muted-foreground">
            Review and approve resolved tickets before they are closed
          </p>
        </div>
        <Button onClick={fetchQueue} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-lg font-semibold">{total}</span>
              <span className="text-muted-foreground">need human review</span>
            </div>
            {total < (tickets.length > 0 ? tickets[0]?.all_pending || total : total) && (
              <div className="text-sm text-muted-foreground">
                (AI auto-processed others)
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Tickets with scores 60-79 or AI review issues are escalated here. 
            High quality (80+) auto-approve, low quality (&lt;60) auto-reject.
          </p>
        </CardContent>
      </Card>

      {/* Queue List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-semibold">All caught up!</h3>
            <p className="text-muted-foreground">No tickets pending approval</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <Card key={ticket.ticket_id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-sm text-muted-foreground">
                        #{ticket.ticket_number}
                      </span>
                      <Badge className={getPriorityColor(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                      {ticket.ai_review?.grade && (
                        <Badge variant="outline" className="ml-2">
                          Quality: {ticket.ai_review.grade} ({ticket.ai_review.score}/100)
                        </Badge>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-lg truncate mb-1">
                      {ticket.summary}
                    </h3>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <span>Requester: {ticket.created_by_name || ticket.created_by_email || 'Unknown'}</span>
                      <span>Assigned: {ticket.assigned_to || 'Unassigned'}</span>
                      <span>Resolved: {formatDate(ticket.resolved_on)}</span>
                    </div>
                    
                    {ticket.ai_review?.summary && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                        AI Review: {ticket.ai_review.summary}
                      </p>
                    )}
                    
                    {ticket.ai_review?.issues?.length > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm">{ticket.ai_review.issues.length} issue(s) noted</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/tickets/managed/${ticket.ticket_id}`)}
                      data-testid={`view-ticket-${ticket.ticket_number}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(ticket)}
                      disabled={processing}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`approve-ticket-${ticket.ticket_number}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openRejectDialog(ticket)}
                      disabled={processing}
                      data-testid={`reject-ticket-${ticket.ticket_number}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4">
            Page {page} of {Math.ceil(total / 20)}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 20)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Ticket Closure</DialogTitle>
            <DialogDescription>
              This will reopen ticket #{selectedTicket?.ticket_number} and return it to "In Progress" status.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              Reason for rejection (required)
            </label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this ticket cannot be closed yet..."
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectReason.trim() || processing}
              data-testid="confirm-reject-btn"
            >
              {processing ? 'Processing...' : 'Reject & Reopen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
